import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { SessionContext } from '../contexts/SessionContext';
import { APIContext } from '../contexts/APIContext';
import { ChatbotContext } from '../contexts/ChatbotContext';
import { ToastAction } from './ui/toast/Toast';
import { useToast } from './ui/toast/UseToast';
import { Response } from '../types';
import { trackUserClicks, getUserEnterpriseId, getValidToken } from '../utils';

export const ChatDownload = (props: any) => {
  const { chatDownload, setChatDownload } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [selectedOption, setSelectedOption] = useState('Select');
  const { downloadChatHistory } = useContext(SessionContext);
  const {
    websocketConnectionRef: { current: websocket },
    userDetails,
    chatSessionId,
    WBUId,
    AppName,
    accounts,
  } = useContext(APIContext);
  const { chatPromptResponseHistory } = useContext(ChatbotContext);
  const { toast } = useToast();
  const [hoveredPPT, setHoveredPPT] = useState(false);
  const [hoveredDOCX, setHoveredDOCX] = useState(false);

  const handleCancel = () => {
    setChatDownload(false);
  };

  const toggleOptions = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
    setIsOpen(false);
  };

  //Function to generate request obj for ppt download
  const buildGenerateRequest = (accounts: any, accessToken: any) => {
    const stories = [...chatPromptResponseHistory];
    return {
      function: 'export_history',
      userId: localStorage.getItem('gamma-userid') || crypto.randomUUID(),
      stories: stories,
      FileName: fileName,
      FileType: selectedOption,
      EnterpriseId: getUserEnterpriseId(accounts),
      SessionUId: chatSessionId,
      authorization: `Bearer ${accessToken}`,
    };
  };

  const handleExportClick = async () => {
    const token = await getValidToken();
    setChatDownload(false);
    if (selectedOption === 'DOCX') {
      toast({
        status: 'loading',
        description: 'Your DOCX is being generated...',
      });
      trackUserClicks(websocket, 'Docx Download', userDetails, chatSessionId, WBUId, AppName, accounts, token);
      handleExportFile();
    } else if (selectedOption === 'PPTX') {
      toast({
        status: 'loading',
        description: 'Your PowerPoint  is being generated...',
      });
      trackUserClicks(websocket, 'PPT Download', userDetails, chatSessionId, WBUId, AppName, accounts, token);
      handleExportFile();
    }
  };

  const handleExportFile = async () => {
    // api call
    const token = await getValidToken();
    const request = buildGenerateRequest(accounts, token);
    console.log('Generate PPT Request: ', request);
    // Send the request to the backend - websocket will never be null here
    websocket!.send('SendMessage', request);
  };
  // Function to check if the Save button should be enabled
  const isSaveButtonEnabled = fileName.length >= 3 && fileName.length <= 200 && selectedOption !== 'Select';

  // Function to filter out special characters from the file name
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    // Filter out special characters using regex
    const newValue = value.replace(/[^\w\s&-]/gi, '');
    setFileName(newValue);
  };

  return (
    <>
      <Transition.Root show={chatDownload} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={() => setChatDownload(true)}>
          {/* Opaque overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30 transition-opacity" />
          </Transition.Child>

          {/* Modal panel */}
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-4"
              >
                <div className="flex flex-col items-center flex-shrink-0 lg:w-1/2">
                  <Dialog.Panel className="relative flex border-t-4 border-t-[#A100FF] w-[582px] h-[492px] transform flex-col bg-gamma-modal-bg font-graphik text-black transition-all">
                    <div className="flex flex-row justify-end pr-[24px] pt-[28px]">
                      <img
                        src="/icons/Close.svg"
                        alt="Send suggested prompt"
                        className="w-[16px] h-[16px] cursor-pointer"
                        onClick={handleCancel}
                      />
                    </div>
                    <div className="relative flex transform flex-col bg-[var(--Surface-card-bg, #FFF)] shadow-md pt-[18px] pl-[40px] pb-[89px] gap-5">
                      <Dialog.Title className="text-black font-graphik text-[28px] font-normal leading-[28px]">
                        Chat Download
                      </Dialog.Title>
                      <Dialog.Description className="text-black font-graphik text-[14px] font-normal leading-[20px]">
                        File Name
                      </Dialog.Description>
                      <input
                        type="text"
                        className="w-[352px] h-[48px] text-gray-700 border border-[#ccc] p-4 text-xs"
                        placeholder="Untitled_1"
                        value={fileName}
                        onChange={handleFileNameChange}
                        tabIndex={-1}
                        maxLength={100}
                      ></input>
                      <Dialog.Description className="text-black font-graphik text-[14px] font-normal leading-[20px]">
                        File Format
                      </Dialog.Description>
                      <div className="relative inline-block">
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => toggleOptions()}
                            className={`flex justify-between items-center w-[352px] h-[48px] text-gray-700 border border-[#ccc] p-4 mt-2 relative ${
                              isOpen ? 'border-2 border-solid border-black rounded' : ''
                            }`}
                          >
                            <p className="text-xs">{selectedOption}</p>
                            <img className="w-[5%]" alt="down" src="/icons/Down.svg" />
                          </button>
                          <ul
                            className={`absolute z-10 ml-[14rem] w-[126px] h-[96px] text-sm ${
                              isOpen ? 'block' : 'hidden'
                            }`}
                          >
                            <li
                              className="h-[48px] px-4 py-2 cursor-pointer flex items-center hover:text-[#A100FF]"
                              onMouseEnter={() => {
                                setHoveredPPT(true);
                              }}
                              onMouseLeave={() => {
                                setHoveredPPT(false);
                              }}
                              onClick={() => handleOptionClick('PPTX')}
                            >
                              <img
                                alt="PPT"
                                src={hoveredPPT ? '/icons/PPT_Active.svg' : '/icons/PPT.svg'}
                                className="mr-2"
                              />
                              PPTX
                            </li>
                            <li
                              className="h-[48px]  px-4 py-2 cursor-pointer flex items-center hover:text-[#A100FF]"
                              onMouseEnter={() => {
                                setHoveredDOCX(true);
                              }}
                              onMouseLeave={() => {
                                setHoveredDOCX(false);
                              }}
                              onClick={() => handleOptionClick('DOCX')}
                            >
                              <img
                                alt="DOCX"
                                src={hoveredDOCX ? '/icons/Doc_Active.svg' : '/icons/DOC.svg'}
                                className=" mr-2"
                              />
                              DOCX
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-row flex-nowrap justify-end gap-5 pr-[32px] pt-[24px]">
                      <button
                        className="rounded-sm border border-[#000]  h-[40px] w-[108px] px-4 py-2 hover:text-[#460073] hover:border-[#460073]"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                      <button
                        className={`rounded-sm  px-4 py-2 h-[40px] w-[108px] text-white ${
                          isSaveButtonEnabled ? 'bg-[#000000] hover:bg-[#460073]' : 'bg-[#CCCCCC]'
                        }`}
                        onClick={handleExportClick}
                        disabled={!isSaveButtonEnabled}
                      >
                        Save
                      </button>
                    </div>
                  </Dialog.Panel>
                </div>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ChatDownload;
